import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Space, Image } from 'antd';
import { Table } from 'components/common/Table/Table';
import { ColumnsType } from 'antd/es/table';
import { Card } from '@app/components/common/Card/Card';
import { notificationController } from '@app/controllers/notificationController';
import { Popconfirm } from '@app/components/common/Popconfirm/Popconfirm';
import { Button } from '@app/components/common/buttons/Button/Button';
import { useMounted } from '@app/hooks/useMounted';
import { PlusOutlined } from '@ant-design/icons';
import { deleteInspiration, getInspirationList, InspirationModel } from '@app/api/inspiration.api';
import { CreateInspirationModal } from '@app/components/inspiration/CreateInspirationModal';
import { UpdateInspirationModal } from '@app/components/inspiration/UpdateInspirationModal';

const InspirationPage: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: InspirationModel[];
    loading: boolean;
  }>({
    data: [],
    loading: false,
  });

  const { isMounted } = useMounted();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [currentInspiration, setCurrentInspiration] = useState<InspirationModel>();

  const fetch = useCallback(() => {
    setTableData((tableData) => ({ ...tableData, loading: true }));
    getInspirationList().then((res) => {
      setTableData({
        data: res.data,
        loading: false,
      });
    });
  }, [isMounted]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleDelete = (rowId: string) => {
    deleteInspiration(rowId).then((res) => {
      notificationController.success({
        message: 'Delete coupon',
        description: 'Successfully',
      });
      fetch();
    });
  };

  const hideCreateModal = () => {
    setIsCreateOpen(false);
  };

  const hideEditModal = () => {
    setIsEditOpen(false);
  };

  const columns: ColumnsType<InspirationModel> = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 190,
      render: (imageUrl: string, record, index) => {
        const _url = `${imageUrl}?alt=media&xplorehash=${record.updatedAt}`;
        return (
          <Space size="middle">
            <Image key={`form_thumbnail_${index}`} preview={false} width={150} src={_url} />
          </Space>
        );
      },
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      width: 130,
    },
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
      width: 30,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (id, record) => {
        return (
          <Space wrap>
            <Button
              type="link"
              onClick={() => {
                setCurrentInspiration(record);
                setIsEditOpen(true);
              }}
            >
              Edit
            </Button>
            <Popconfirm
              title="Are you sure to delete this inspiration?"
              okText="Yes"
              cancelText="No"
              placement="left"
              onConfirm={() => handleDelete(id)}
            >
              <Button type="link">Delete</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card
        padding="0 1.875rem 1.875rem 1.875rem"
        title="Inspiration list"
        extra={
          <div style={{ display: 'flex' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreateOpen(true)}>
              Create
            </Button>
          </div>
        }
      >
        <Table
          columns={columns}
          pagination={false}
          dataSource={tableData.data}
          loading={tableData.loading}
          scroll={{ x: 1300 }}
          bordered
          rowKey={(record: InspirationModel) => record.id}
          key={'inspiration_table'}
        />
      </Card>
      <CreateInspirationModal
        open={isCreateOpen}
        onCancel={hideCreateModal}
        onFinish={() =>
          setTimeout(() => {
            hideCreateModal();
            fetch();
          }, 250)
        }
      />
      <UpdateInspirationModal
        data={currentInspiration}
        open={isEditOpen}
        onCancel={() => {
          setCurrentInspiration(undefined);
          hideEditModal();
        }}
        onFinish={() =>
          setTimeout(() => {
            setCurrentInspiration(undefined);
            fetch();
            hideEditModal();
          }, 250)
        }
      />
    </>
  );
};

export default InspirationPage;
